// İSG Portal — Risk Değerlendirme (Risk Assessment — tenant-scoped) API çağrıları + tipleri.
// **İSG uzmanı tarafı** (web-expert): değerlendirme CRUD + yaşam döngüsü (submit/approve/
// review/revise/archive) + tehlike kalemleri (RiskItem) + DÖF (RiskAction) + artık risk.
//
// KAYNAK: api-contract.md §19 (Risk Değerlendirme). Backend kanonu; burada **varsayım YOK** —
// endpoint yolları, istek/yanıt alanları ve kimlik kuralları (Result unwrap `value`; Create →
// `value.uuid` + 201; liste → `value` + `total`/`filteredTotal`) doğrudan sözleşmeden
// türetilmiştir. Serileştirme camelCase (§1).
//
// **Tenant izolasyonu (§4b/§19).** Tüm uçlar çağıranın firmasına (token `CompanyId`) kapanır;
// istemci firma kimliği GÖNDERMEZ — sunucu token'dan çözer. Başka firmanın değerlendirmesi/
// kalemi/DÖF'ü hiçbir koşulda görünmez (yok gibi `404 RISK_ASSESSMENT_NOT_FOUND`). Silme
// **yoktur** (terminal durum = `Archived`); kalem/DÖF kaldırma **soft-delete**.
//
// **⛔ Skor/band SUNUCU türetir — istemci GÖNDERMEZ (BAĞLAYICI, §19).** İstemci kalem için
// yalnız `probability`/`severity` (1-5) ve varsa `residualProbability`/`residualSeverity`
// gönderir. `score`/`level`/`residualScore`/`residualLevel`/`highestLevel` **yalnız yanıtta**
// döner (sunucu türetir); istemci bunları yalnız gösterir (yeniden hesaplamaz).

import { api } from './client';
import type { Empty } from './envelope';

/**
 * Değerlendirme durumu — wire değerleri PascalCase (§19 `RiskAssessmentDto.status`). Yaşam
 * döngüsü: `Draft` → `Submitted` → `Approved`/`Active` → `UnderReview` → `Revised` →
 * (`Submitted` …); herhangi bir durumdan `Archived` (terminal). Düzenlenebilir statüler:
 * `Draft`/`Submitted`/`UnderReview`/`Revised`. Kilitli: `Approved`/`Active`/`Archived`.
 */
export type RiskAssessmentStatus =
  | 'Draft'
  | 'Submitted'
  | 'Approved'
  | 'Active'
  | 'UnderReview'
  | 'Revised'
  | 'Archived';

/**
 * Metodoloji — şimdilik yalnız 5x5 L-tipi matris (§19; Fine-Kinney ileri uyum için enum).
 * Değerlendirme oluşturulduktan sonra **değiştirilemez** (PUT gövdesinde yoktur).
 */
export type RiskMethodology = 'LType5x5';

/**
 * 5x5 L-tipi bant — **sunucu türetir** (`probability × severity`), saklanmaz (§19). Enum
 * değerleri ASCII; görünen metin (Önemsiz/Düşük/Orta/Yüksek/Kabul Edilemez) FE i18n'inde.
 * Bantlar: `score ≤ 2` Onemsiz · `≤ 6` Dusuk · `≤ 12` Orta · `≤ 20` Yuksek · `> 20` KabulEdilemez.
 */
export type RiskLevel = 'Onemsiz' | 'Dusuk' | 'Orta' | 'Yuksek' | 'KabulEdilemez';

/** DÖF (Düzeltici/Önleyici Faaliyet) durumu — wire PascalCase (§19 `RiskActionDto.status`). */
export type RiskActionStatus = 'Open' | 'InProgress' | 'Done' | 'Cancelled';

/**
 * Risk değerlendirme (liste satırı) — api-contract §19 (`RiskAssessmentDto`). Sıralı iç `Id`
 * istemciye **sızmaz** (yalnız `uuid`). `assessmentDate`/`approvedAt`/`nextReviewDate`/
 * `createdAt` UTC ISO (§4). `highestLevel` = kalemler arasındaki **en yüksek** türetilmiş band
 * (kalem yoksa `null`). `siteName`/`assessedByName`/`approvedByName` ilgili store'lardan çözülür.
 */
export interface RiskAssessmentDto {
  uuid: string;
  title: string;
  siteUuid: string;
  siteName: string | null;
  area: string | null;
  /** Değerlendirme tarihi — UTC ISO (§4; DateTime). */
  assessmentDate: string;
  status: RiskAssessmentStatus;
  methodology: RiskMethodology;
  assessedByName: string | null;
  approvedByName: string | null;
  /** Onay anı — UTC ISO (§4); onaylanmışsa dolu. */
  approvedAt: string | null;
  /** Sonraki gözden geçirme (geçerlilik) tarihi — UTC ISO (§4); opsiyonel. */
  nextReviewDate: string | null;
  revisionNo: number;
  itemCount: number;
  /** Türetilmiş en yüksek kalem bandı; kalem yoksa `null` (**sunucu türetir**). */
  highestLevel: RiskLevel | null;
  createdAt: string | null;
}

/**
 * DÖF (Düzeltici/Önleyici Faaliyet) — api-contract §19 (`RiskActionDto`). `responsibleUserId`
 * = sorumlu kullanıcının **public uuid**'i (verilirse aynı firmada); `responsibleName`
 * identity'den çözülür; `completedAt` `Done`'a geçişte sunucu set eder (çıkışta temizlenir).
 */
export interface RiskActionDto {
  uuid: string;
  description: string;
  responsibleUserId: string | null;
  responsibleName: string | null;
  /** Termin tarihi — UTC ISO (§4); opsiyonel. */
  dueDate: string | null;
  status: RiskActionStatus;
  /** Tamamlanma anı — UTC ISO (§4); `Done` durumunda dolu. */
  completedAt: string | null;
  note: string | null;
}

/**
 * Tehlike kalemi — api-contract §19 (`RiskItemDto`). `score`/`level` = **sunucu türetir**
 * (`probability × severity` + band); `residualScore`/`residualLevel` yalnız her iki residual
 * faktör de doluysa (aksi `null`). İstemci `score`/`level`/`residual*` türevlerini **göndermez**,
 * yalnız gösterir. `actions` = kalemin DÖF listesi.
 */
export interface RiskItemDto {
  uuid: string;
  order: number;
  hazard: string;
  risk: string | null;
  affectedPersons: string | null;
  existingControls: string | null;
  /** Olasılık (1-5). */
  probability: number;
  /** Şiddet (1-5). */
  severity: number;
  /** `probability × severity` — **sunucu türetir**. */
  score: number;
  /** 5x5 band — **sunucu türetir** (ASCII). */
  level: RiskLevel;
  recommendedControls: string | null;
  /** Artık risk olasılığı (1-5); opsiyonel. */
  residualProbability: number | null;
  /** Artık risk şiddeti (1-5); opsiyonel. */
  residualSeverity: number | null;
  /** Artık `residualProbability × residualSeverity` — **sunucu türetir**; ikisi de doluysa. */
  residualScore: number | null;
  /** Artık risk bandı — **sunucu türetir**; ikisi de doluysa (aksi `null`). */
  residualLevel: RiskLevel | null;
  actions: RiskActionDto[];
}

/**
 * Risk değerlendirme detayı — api-contract §19 (`RiskAssessmentDetailDto`,
 * `GET /risk-assessments/{uuid}`). Değerlendirme + kalemler (`order` artan; her kalem türetilmiş
 * score/level + DÖF'leriyle).
 */
export interface RiskAssessmentDetailDto {
  assessment: RiskAssessmentDto;
  items: RiskItemDto[];
}

/**
 * Kalem yazma girdisi (inline create + kalem ekle/güncelle) — api-contract §19.1/§19.3.
 * `hazard` zorunlu; `probability`/`severity` 1-5; residual ya birlikte ya hiç. `score`/`level`
 * **gönderilmez** (sunucu türetir). `order` verilmezse mevcut en yüksek + 1.
 */
export interface RiskItemWriteRequest {
  hazard: string;
  risk?: string | null;
  affectedPersons?: string | null;
  existingControls?: string | null;
  probability: number;
  severity: number;
  recommendedControls?: string | null;
  residualProbability?: number | null;
  residualSeverity?: number | null;
  order?: number;
}

/**
 * Değerlendirme oluşturma isteği — api-contract §19.1 (`POST /risk-assessments`). `siteUuid`
 * çağıranın firmasında bir saha olmalı (yok/boş → `404 SITE_NOT_FOUND`); `title`/`assessmentDate`
 * zorunlu. `items` (opsiyonel) inline tehlike kalemleri — herhangi biri geçersizse **hiç
 * yazılmaz**. Yeni değerlendirme `status = Draft`; `assessedByUserId` token'dan (istekte yok).
 * **DÖF create gövdesinde yoktur** — kalem oluştuktan sonra §19.4 uçlarıyla eklenir.
 */
export interface RiskAssessmentCreateRequest {
  title: string;
  siteUuid: string;
  area?: string | null;
  /** UTC ISO (§4; DateTime). */
  assessmentDate: string;
  /** UTC ISO (§4; DateTime); opsiyonel. */
  nextReviewDate?: string | null;
  notes?: string | null;
  items?: RiskItemWriteRequest[];
}

/**
 * Değerlendirme güncelleme isteği — api-contract §19.1 (`PUT /risk-assessments/{uuid}`). Yalnız
 * düzenlenebilir statüde (aksi → `409 RISK_ASSESSMENT_NOT_EDITABLE`). `siteUuid` aynı firmada
 * (`404 SITE_NOT_FOUND`). Metodoloji **değiştirilemez** (istekte yok). Kalemler PUT ile değişmez —
 * §19.3 uçlarıyla yönetilir.
 */
export interface RiskAssessmentUpdateRequest {
  title: string;
  siteUuid: string;
  area?: string | null;
  assessmentDate: string;
  nextReviewDate?: string | null;
  notes?: string | null;
}

/**
 * DÖF yazma isteği — api-contract §19.4 (`POST`/`PUT` .../actions). `description` zorunlu.
 * `responsibleUserId` verilirse aynı firmada olmalı (`404 USER_NOT_FOUND`); `null` → sorumlu
 * bağı kaldırılır. `status` POST'ta opsiyonel (verilmezse `Open`); PUT'ta zorunlu + tanınmalı.
 * `Done`'a geçişte `completedAt` sunucu set eder.
 */
export interface RiskActionWriteRequest {
  description: string;
  responsibleUserId?: string | null;
  dueDate?: string | null;
  status?: RiskActionStatus;
  note?: string | null;
}

/**
 * Değerlendirme liste/filtre parametreleri — api-contract §19.1 (`GET /risk-assessments`) + §3.
 * `status` (case-insensitive), `siteUuid` (firma içinde çözülür), `level` (türetilmiş
 * `highestLevel`; case-insensitive), `from`/`to` (`assessmentDate` aralığı — UTC ISO), `search`
 * (başlık substring). Sıra backend'de `assessmentDate` azalan (istemci sort göndermez).
 */
export interface RiskAssessmentListParams {
  status?: RiskAssessmentStatus;
  siteUuid?: string;
  level?: RiskLevel;
  /** `assessmentDate` alt sınırı — UTC ISO (§4). */
  from?: string;
  /** `assessmentDate` üst sınırı — UTC ISO (§4). */
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

// ============================================================================
// 19.1 Değerlendirmeler (CRUD)
// ============================================================================

/**
 * `GET /risk-assessments` (`list`) — firmanın risk değerlendirmeleri (sayfalı + filtreli).
 * Sıra `assessmentDate` azalan (istemci sort göndermez). `highestLevel` backend'de türetilir.
 */
export function listRiskAssessments(params: RiskAssessmentListParams = {}) {
  return api.list<RiskAssessmentDto>('risk-assessments', {
    searchParams: {
      status: params.status,
      siteUuid: params.siteUuid,
      level: params.level,
      from: params.from,
      to: params.to,
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `GET /risk-assessments/{uuid}` (`get`) — tek değerlendirme + kalemler (+ her kalemin DÖF'leri
 * + türetilmiş score/level). Yok/kapsam dışı → `404 RISK_ASSESSMENT_NOT_FOUND`.
 */
export function getRiskAssessment(uuid: string) {
  return api.get<RiskAssessmentDetailDto>(`risk-assessments/${uuid}`);
}

/**
 * `POST /risk-assessments` (`create`) — değerlendirme oluşturur (`status = Draft`); yeni kimlik
 * `value.uuid` (201). Doğrulama hataları → `400 VALIDATION_FAILED`; saha yok → `404 SITE_NOT_FOUND`.
 */
export function createRiskAssessment(body: RiskAssessmentCreateRequest) {
  return api.post<RiskAssessmentDto>('risk-assessments', { json: body });
}

/**
 * `PUT /risk-assessments/{uuid}` (`update`) — değerlendirme meta'sını günceller. Yalnız
 * düzenlenebilir statüde (aksi → `409 RISK_ASSESSMENT_NOT_EDITABLE`). Yok/kapsam dışı →
 * `404 RISK_ASSESSMENT_NOT_FOUND`; saha yok → `404 SITE_NOT_FOUND`.
 */
export function updateRiskAssessment(uuid: string, body: RiskAssessmentUpdateRequest) {
  return api.put<RiskAssessmentDto>(`risk-assessments/${uuid}`, { json: body });
}

// ============================================================================
// 19.2 Yaşam döngüsü
// ============================================================================

/** `POST /risk-assessments/{uuid}/submit` (`update`) — `Draft`/`Revised` → `Submitted`. */
export function submitRiskAssessment(uuid: string) {
  return api.post<RiskAssessmentDto>(`risk-assessments/${uuid}/submit`);
}

/**
 * `POST /risk-assessments/{uuid}/approve` (`update`) — `Submitted` → `Approved` (onay anında
 * geçerliyse doğrudan `Active`). `approvedByUserId` (çağıran) + `approvedAt` set. Aksi → `409`.
 */
export function approveRiskAssessment(uuid: string) {
  return api.post<RiskAssessmentDto>(`risk-assessments/${uuid}/approve`);
}

/** `POST /risk-assessments/{uuid}/review` (`update`) — `Active` → `UnderReview` (aksi → `409`). */
export function reviewRiskAssessment(uuid: string) {
  return api.post<RiskAssessmentDto>(`risk-assessments/${uuid}/review`);
}

/**
 * `POST /risk-assessments/{uuid}/revise` (`update`) — `UnderReview` → `Revised`, `revisionNo++`
 * (yeniden düzenlenebilir). Aksi → `409 RISK_ASSESSMENT_INVALID_STATE`.
 */
export function reviseRiskAssessment(uuid: string) {
  return api.post<RiskAssessmentDto>(`risk-assessments/${uuid}/revise`);
}

/** `POST /risk-assessments/{uuid}/archive` (`update`) — herhangi bir durumdan → `Archived` (terminal). */
export function archiveRiskAssessment(uuid: string) {
  return api.post<RiskAssessmentDto>(`risk-assessments/${uuid}/archive`);
}

// ============================================================================
// 19.3 Kalemler (yalnız düzenlenebilir statüde — aksi `409 RISK_ASSESSMENT_NOT_EDITABLE`)
// ============================================================================

/**
 * `POST /risk-assessments/{uuid}/items` (`update`) — tehlike kalemi ekler; yeni kimlik
 * `value.uuid` (201). `score`/`level` gönderilmez (sunucu türetir). Kalem yoksa order = en
 * yüksek + 1. Değerlendirme yok → `404 RISK_ASSESSMENT_NOT_FOUND`.
 */
export function createRiskItem(uuid: string, body: RiskItemWriteRequest) {
  return api.post<RiskItemDto>(`risk-assessments/${uuid}/items`, { json: body });
}

/**
 * `PUT /risk-assessments/{uuid}/items/{itemUuid}` (`update`) — kalemi günceller (aynı
 * doğrulama). Kalem yok → `404 RISK_ITEM_NOT_FOUND`.
 */
export function updateRiskItem(uuid: string, itemUuid: string, body: RiskItemWriteRequest) {
  return api.put<RiskItemDto>(`risk-assessments/${uuid}/items/${itemUuid}`, { json: body });
}

/**
 * `DELETE /risk-assessments/{uuid}/items/{itemUuid}` (`update`) — kalemi kaldırır (**soft-delete**;
 * DÖF'leri de). Kalem yok → `404 RISK_ITEM_NOT_FOUND`.
 */
export function deleteRiskItem(uuid: string, itemUuid: string) {
  return api.delete<Empty>(`risk-assessments/${uuid}/items/${itemUuid}`);
}

// ============================================================================
// 19.4 DÖF (Düzeltici/Önleyici Faaliyet) — yalnız düzenlenebilir statüde
// ============================================================================

/**
 * `POST /risk-assessments/{uuid}/items/{itemUuid}/actions` (`update`) — DÖF ekler; yeni kimlik
 * `value.uuid` (201). `description` zorunlu; `status` verilmezse `Open`; `Done` verilirse
 * `completedAt` sunucu set eder. Kalem yok → `404 RISK_ITEM_NOT_FOUND`; sorumlu başka firma →
 * `404 USER_NOT_FOUND`.
 */
export function createRiskAction(uuid: string, itemUuid: string, body: RiskActionWriteRequest) {
  return api.post<RiskActionDto>(`risk-assessments/${uuid}/items/${itemUuid}/actions`, {
    json: body,
  });
}

/**
 * `PUT /risk-assessments/{uuid}/items/{itemUuid}/actions/{actionUuid}` (`update`) — DÖF günceller.
 * `description` + `status` zorunlu. `Done`'a geçişte `completedAt` set; `Done` dışına çıkışta
 * temizlenir. `responsibleUserId` null → sorumlu bağı kaldırılır. DÖF yok → `404 RISK_ACTION_NOT_FOUND`.
 */
export function updateRiskAction(
  uuid: string,
  itemUuid: string,
  actionUuid: string,
  body: RiskActionWriteRequest,
) {
  return api.put<RiskActionDto>(
    `risk-assessments/${uuid}/items/${itemUuid}/actions/${actionUuid}`,
    { json: body },
  );
}

/**
 * `DELETE /risk-assessments/{uuid}/items/{itemUuid}/actions/{actionUuid}` (`update`) — DÖF'ü
 * kaldırır (**soft-delete**). DÖF yok → `404 RISK_ACTION_NOT_FOUND`.
 */
export function deleteRiskAction(uuid: string, itemUuid: string, actionUuid: string) {
  return api.delete<Empty>(`risk-assessments/${uuid}/items/${itemUuid}/actions/${actionUuid}`);
}
